import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export function SearchableSelect({
  value,
  onValueChange,
  options = [],
  groups = [],
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  triggerClassName,
  contentClassName,
  disabled = false,
  "data-testid": testId,
}) {
  const [open, setOpen] = React.useState(false);

  const normalizedOptions = React.useMemo(() => {
    if (groups.length > 0) return [];
    return options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  }, [options, groups]);

  const selectedLabel = React.useMemo(() => {
    if (!value) return null;
    const found = normalizedOptions.find((o) => o.value === value);
    if (found) return found.label;
    for (const group of groups) {
      const opts = (group.options || []).map((o) =>
        typeof o === "string" ? { value: o, label: o } : o
      );
      const gFound = opts.find((o) => o.value === value);
      if (gFound) return gFound.label;
    }
    return value;
  }, [value, normalizedOptions, groups]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal h-9 rounded-sm border-input bg-transparent px-3 text-sm shadow-sm",
            !value && "text-muted-foreground",
            triggerClassName
          )}
          data-testid={testId}
        >
          <span className="truncate">{selectedLabel || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-[--radix-popover-trigger-width] p-0", contentClassName)}
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {groups.length > 0
              ? groups.map((group) => (
                  <CommandGroup key={group.label} heading={group.label}>
                    {(group.options || []).map((o) => {
                      const opt = typeof o === "string" ? { value: o, label: o } : o;
                      return (
                        <CommandItem
                          key={opt.value}
                          value={opt.label}
                          onSelect={() => {
                            onValueChange(opt.value);
                            setOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              value === opt.value ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {opt.label}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))
              : (
                <CommandGroup>
                  {normalizedOptions.map((opt) => (
                    <CommandItem
                      key={opt.value}
                      value={opt.label}
                      onSelect={() => {
                        onValueChange(opt.value);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === opt.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {opt.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
